import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { screenXsMax, spacer64, spacer80, white } from '@spotify-internal/encore-web';

const slideDown = keyframes`
  0% {
      opacity: 0;
      transform: translateY(-100%);
  }
  100% {
      opacity: 1;
      transform: translateY(0);
  }
`;

const StickyPlaceholder = styled.div`
  display: ${(props) => (props.stickyHeader && props.headerHeight ? 'block' : 'none')};
  height: ${(props) => props.headerHeight};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;

  ${({ headerMargin }) =>
    headerMargin &&
    css`
      margin-block-start: ${headerMargin};
      margin-block-end: ${headerMargin};
    `}

  ${(props) =>
    props.stickyHeader &&
    css`
      box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 3px 0px;
      z-index: 75;
      position: fixed;
      width: 100%;
      top: ${spacer80};
      right: 0;
      padding-left: ${({ headerLeftPadding }) => headerLeftPadding} !important;
      padding-top: 16px;
      padding-right: 32px !important;
      padding-inline-start: 48px;
      background-color: ${white};
      animation: ${slideDown} 0.3s;
      animation-timing-function: cubic-bezier(0.3, 0, 0, 1);
    `}

  @media (max-width: ${screenXsMax}) {
    width: 100%;
    top: ${spacer64};
  }
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const MainRow = styled.div`
  display: flex;
  margin-bottom: ${(props) => (!props.hasNavBar && props.stickyHeader ? '16px' : '0px')};
`;

const NavBar = styled.div`
  ul {
    margin-bottom: 0;
  }
`;

const SideMenu = styled.div`
  align-self: baseline;
`;

const propTypes = {
  /** Optional cover image on the header. You can use the pre- defined EntityImage and EntityImageWrapper. Function argument should be a boolean that represents whether the stickyHeader is active. */
  renderImage: PropTypes.func,
  /** Text containing Entity type, title, and optional byline. Can use EntityTitle. Function argument should be a boolean that represents whether the stickyHeader is active. */
  renderTitle: PropTypes.func.isRequired,
  /** Optional stats related to the entity appearing in a row below the title. Can use EntityAttributeRow and EntityAttribute. */
  attributes: PropTypes.any,
  /** Optional side menu or button actions appearing on the right side of the page. Can be a single button or a list of CTA items. Shrinks to "..." icon context menu in mobile view */
  actions: PropTypes.any,
  /** Optional tabs on bottom of the header, controlling the visible content on the entity page. Can use Encore Web's NavBar, NavBarList, and NavBarListItem components. */
  tabs: PropTypes.node,
  /** Optional boolean value indicating when the header should become sticky. Please pass this same value into the EntityTitle and EntityImageWrapper components. */
  stickyHeader: PropTypes.bool,
  /** e.g. '200px' Optional pixel string value indicating the header's offset from the left. Included for backwards compatibility with side navigation menus. DEPRECATE once all s4x navs use the top nav  */
  sideNavWidth: PropTypes.string,
  /** e.g. Optional pixel string value representing the static header's full height. This allows us to create a placeholder div when the header becomes sticky, allowing for a smooth animation.  */
  headerHeight: PropTypes.string,
  /** e.g. Optional string value representing the amount of margin to add to the top and bottom of the header.  */
  headerMargin: PropTypes.string,
  /** Optional offset left when header is sticky. Default is 48px */
  stickyOffsetLeft: PropTypes.string,
};

export default function EntityHeader({
  renderImage,
  renderTitle,
  attributes,
  actions,
  tabs,
  stickyHeader,
  sideNavWidth = '0px',
  headerHeight,
  headerMargin,
  stickyOffsetLeft,
}) {
  const headerLeftPadding = stickyOffsetLeft || (sideNavWidth === '0px' ? '48px' : '32px');
  const hasNavBar = !!tabs;
  return (
    <React.Fragment>
      <StickyPlaceholder stickyHeader={stickyHeader} headerHeight={headerHeight} />
      <Header
        stickyHeader={stickyHeader}
        sideNavWidth={sideNavWidth}
        headerLeftPadding={headerLeftPadding}
        headerMargin={headerMargin}
      >
        <div>
          <MainRow stickyHeader={stickyHeader} hasNavBar={hasNavBar}>
            {renderImage && renderImage(stickyHeader)}
            <Center>
              {renderTitle(stickyHeader)}
              {!stickyHeader && attributes}
            </Center>
          </MainRow>
          {hasNavBar && <NavBar stickyHeader={stickyHeader}>{tabs}</NavBar>}
        </div>
        <SideMenu>{actions}</SideMenu>
      </Header>
    </React.Fragment>
  );
}

EntityHeader.propTypes = propTypes;
